import React from 'react';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { BinScatterMultiSelectRequest } from 'libs/end-to-end/src/custom-viz/BinScatterMultiSelectTypes';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { HeatmapAnalysisRequest } from 'libs/end-to-end/src/custom-viz/HeatmapAnalysisTypes';
import { PipelineAnalysisRequest } from 'libs/end-to-end/src/custom-viz/PipelineAnalysisTypes';
import { ProgressRequest } from 'libs/end-to-end/src/custom-viz/ProgressTypes';
import { SuccessPhenotypeV2Request } from 'libs/end-to-end/src/custom-viz/SuccessPhenotypeTypes';
import { getDeepDivePrimaryFilterSelectionsFromRequest } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getTreatmentGroup } from 'libs/end-to-end/src/deep-dive-section-utils';
import { getDimension } from 'libs/end-to-end/src/dimension-utils';
import { FiltersFeatureName } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSelectedSecondaryBreakdownFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { formatDateToYYYYMMDD } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getChartTypeFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getBigTimeComparisonValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getListValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getStringValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getDimensionFilterValue } from 'libs/end-to-end/src/progress-page-utils';
import { getPrimaryFilterSelectionsFromRequest } from 'libs/end-to-end/src/progress-page-utils';
import { getPipelineAnalysisConfigurationFilterValue } from 'libs/end-to-end/src/progress-page-utils';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { getPipelineAnalysisPrimaryFilterSelectionsFromRequest } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysisFullInterface';
import { getExplorePrimaryFilterSelectionsFromRequest } from 'apps/sigma2-ui/src/explore-section/components/ExploreSectionInterface';
import { getLevelByColumnValueFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getPrivilegedLevelForDimensionFromObservation } from 'libs/end-to-end/src/dimension-config-utils';
import { getSuccessFactorsPrimaryFilterSelections } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { CustomVizId } from 'libs/end-to-end/src/custom-viz/CustomVizId';
import { SummaryStatsRequest } from 'libs/end-to-end/src/summary-stats';
import { RichTextRequest } from 'libs/end-to-end/src/custom-viz/RichTextTypes';
import { TableViewRequest } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { getTableViewPrimaryFilterSelections } from 'apps/sigma2-ui/src/explore-section/components/TableView';
import { TimeSeriesTrendsRequest } from 'libs/end-to-end/src/custom-viz/TimeSeriesTrendsTypes';
import { getDateRangeFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getViewByValueFromPrimaryFilterValues } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { GeolocationRequest } from 'libs/end-to-end/src/custom-viz/GeolocationTypes';
import { StreamlitCustomVizRequest } from 'libs/end-to-end/src/custom-viz/StreamlitCustomVizTypes';
import { getTimeSeriesTrendsPrimaryFilterSelections } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChartCard';
import { parse } from 'date-fns';
import { isValid } from 'date-fns';
import { EVENT_STUDY_DATE_FORMAT_PATTERN } from 'libs/end-to-end/src/progress';
import { isEventStudyEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { getSummaryStatsPrimaryFilterSelections } from 'apps/sigma2-ui/src/explore-section/components/SummaryStats';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { resolveAnalysisNameForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';
import { resolveFocusAreaForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';

interface GetCustomVizFromFiltersProps {
  feature: FiltersFeatureName;
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  extras?: Record<string, any>;
  myObservation: MyObservation;
  organizationId: string;
}

const buildBinScatterMultiSelectViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): BinScatterMultiSelectRequest | null => {
  const dimension = getDimensionFilterValue(primaryFilterSelections);

  const focusAreaMetric = getFocusArea(primaryFilterSelections);

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  if (!dimension || !focusAreaMetric) {
    return null;
  }

  return {
    customVizId: 'binScatterMultiSelect',
    inputs: {
      extras: {
        filters,
        metric: focusAreaMetric.metric,
        dimension,
        focusArea: focusAreaMetric.focusArea,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

const buildPipelineAnalysisViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): PipelineAnalysisRequest | null => {
  const dimension = getDimensionFilterValue(primaryFilterSelections);
  const pipelineAnalysisConfiguration =
    getPipelineAnalysisConfigurationFilterValue(primaryFilterSelections);

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  if (!dimension || !pipelineAnalysisConfiguration || !myObservation?.type) {
    return null;
  }

  return {
    customVizId: 'pipelineAnalysis',
    organizationId,
    observationLocationType: {
      type: 'db-revision',
      databaseRevisionId: myObservation.type.databaseRevisionId,
      databaseId: myObservation.type.databaseId,
    },
    inputs: {
      dropdowns: {},
      extras: {
        filters,
        dimension,
        analysisName: pipelineAnalysisConfiguration,
      },
    },
  };
};

const buildDeepDiveViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): BiasAnalysisRequest | null => {
  const treatmentGroup = getTreatmentGroup(primaryFilterSelections);
  const dimension = getDimension(primaryFilterSelections);

  const focusArea = getFocusArea(primaryFilterSelections);

  if (!focusArea || !treatmentGroup || !dimension) {
    return null;
  }

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'biasAnalysis',
    organizationId,
    observationLocationType: myObservation.type,
    inputs: {
      extras: {
        dimension,
        focusArea: focusArea.focusArea,
        metric: focusArea.metric,
        filters,
        treatmentDimensionLevel: treatmentGroup,
      },
      dropdowns: {},
    },
  };
};

const buildHeatmapViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): HeatmapAnalysisRequest | null => {
  const dimension = getDimension(primaryFilterSelections);

  const treatmentGroup = getTreatmentGroup(primaryFilterSelections);

  const metrics =
    getListValueFromPrimaryFilterValues({
      primaryFilterKey: 'metrics',
      primaryFilterValues: primaryFilterSelections,
    }) ?? [];

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  if (!treatmentGroup || !dimension) {
    return null;
  }

  return {
    customVizId: 'heatmap',
    organizationId,
    observationLocationType: myObservation.type,
    inputs: {
      extras: {
        dimension,
        filters,
        treatmentDimensionLevel: treatmentGroup,
        metrics,
      },
      dropdowns: {},
    },
  };
};

export const buildSuccessPhenotypeViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: Omit<
  GetCustomVizFromFiltersProps,
  'feature'
>): SuccessPhenotypeV2Request | null => {
  const focusArea = getFocusArea(primaryFilterSelections);

  if (!focusArea) {
    return null;
  }

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'successPhenotypeV2',
    inputs: {
      extras: {
        filters,
        metric: focusArea.metric,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

const buildProgressViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): ProgressRequest | null => {
  const focusArea = getFocusArea(primaryFilterSelections);
  const dimension = getDimensionFilterValue(primaryFilterSelections);

  if (!focusArea || !dimension || !myObservation?.type) {
    return null;
  }

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'progress',
    organizationId,
    observationLocationType: myObservation?.type,
    inputs: {
      extras: {
        focusArea: focusArea.focusArea,
        dimension,
        metric: focusArea.metric,
        filters: filters,
      },
      dropdowns: {},
    },
  };
};

// eslint-disable-next-line complexity
export const buildTimeSeriesTrendsViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): TimeSeriesTrendsRequest | null => {
  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  const groupBy = getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'groupBy',
    primaryFilterValues: primaryFilterSelections,
  });

  const dateRange = getDateRangeFromPrimaryFilterValues({
    primaryFilterValues: primaryFilterSelections,
  });

  // Determine the selected "view by" granularity (day/week/month/year)
  const viewByValue = getViewByValueFromPrimaryFilterValues({
    primaryFilterValues: primaryFilterSelections,
  });

  const bigTimePeriod = getBigTimeComparisonValueFromPrimaryFilterValues({
    primaryFilterValues: primaryFilterSelections,
  });

  const fa = getFocusArea(primaryFilterSelections);

  if (!fa || !viewByValue) {
    return null;
  }

  const chartType = getChartTypeFromPrimaryFilterValues({
    primaryFilterValues: primaryFilterSelections,
  });

  const treatmentDate = getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'singleDate',
    primaryFilterValues: primaryFilterSelections,
  });

  const parsedTreatmentDate = treatmentDate
    ? parse(treatmentDate, EVENT_STUDY_DATE_FORMAT_PATTERN, new Date())
    : null;

  const treatmentDateValue =
    parsedTreatmentDate && isValid(parsedTreatmentDate)
      ? formatDateToYYYYMMDD(parsedTreatmentDate)
      : null;

  const treatmentGroupList = getListValueFromPrimaryFilterValues({
    primaryFilterKey: 'eventStudyGroup',
    primaryFilterValues: primaryFilterSelections,
  });

  const treatmentGroup =
    treatmentGroupList && treatmentDateValue !== null
      ? treatmentGroupList[1]
      : null;

  const isEventStudyEnabled = isEventStudyEnabledForRevision({
    revisionFlags: myObservation,
  });

  const bigTimeComparisonOption =
    isEventStudyEnabled && treatmentDateValue !== null
      ? 'none'
      : (bigTimePeriod ?? 'none');

  return {
    customVizId: 'timeSeriesTrends',
    organizationId,
    observationLocationType: myObservation.type,
    inputs: {
      extras: {
        metric: fa.metric,
        breakdownOption: groupBy || 'none',
        filters: filters,
        timeBoiOption: viewByValue ?? 'year',
        bigTimeComparisonOption,
        startDate: formatDateToYYYYMMDD(dateRange?.startDate || new Date()),
        endDate: formatDateToYYYYMMDD(dateRange?.endDate || new Date()),
        dateRangeType: dateRange?.dateRangeType || 'all',
        chartType:
          isEventStudyEnabled && treatmentDateValue ? 'line' : chartType,
        treatmentDate: treatmentDateValue,
        treatmentGroup,
      },
      dropdowns: {},
    },
  };
};

const buildGeolocationViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): GeolocationRequest | null => {
  const metrics = getListValueFromPrimaryFilterValues({
    primaryFilterKey: 'metrics',
    primaryFilterValues: primaryFilterSelections,
  });

  if (!metrics) {
    return null;
  }

  const dateRange = getDateRangeFromPrimaryFilterValues({
    primaryFilterValues: primaryFilterSelections,
  });

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'geolocation',
    organizationId,
    observationLocationType: myObservation.type,
    inputs: {
      dropdowns: {},
      extras: {
        metrics,
        startDate: formatDateToYYYYMMDD(dateRange?.startDate || new Date()),
        endDate: formatDateToYYYYMMDD(dateRange?.endDate || new Date()),
        dateRangeType: dateRange?.dateRangeType || 'all',
        filters,
      },
    },
  };
};

const buildSummaryStatsViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): SummaryStatsRequest | null => {
  const metrics = getListValueFromPrimaryFilterValues({
    primaryFilterKey: 'metrics',
    primaryFilterValues: primaryFilterSelections,
  });

  if (!metrics) {
    return null;
  }

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'summaryStats',
    inputs: {
      extras: {
        metrics,
        filters,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

const buildRichTextViz = ({
  extras,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): RichTextRequest | null => {
  if (!extras?.richText) {
    return null;
  }

  return {
    customVizId: 'richText',
    inputs: {
      extras: {
        richText: extras?.richText,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

const buildExternalStreamlitViz = ({
  extras,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): StreamlitCustomVizRequest | null => {
  // Get streamlitAppId from extras, or use a placeholder if not provided
  console.log('extras', extras);
  const streamlitAppId = extras?.streamlitAppId;

  if (!streamlitAppId) {
    return null;
  }

  return {
    customVizId: 'externalStreamlit',
    inputs: {
      extras: {
        streamlitAppId,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

const buildTableViewViz = ({
  primaryFilterSelections,
  secondaryFilterSelections,
  myObservation,
  organizationId,
}: GetCustomVizFromFiltersProps): TableViewRequest | null => {
  const groupBy = getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'groupBy',
    primaryFilterValues: primaryFilterSelections,
  });

  const secondaryBreakdown =
    getSelectedSecondaryBreakdownFromPrimaryFilterValues({
      primaryFilterValues: primaryFilterSelections,
    }) || undefined;

  const metrics = getListValueFromPrimaryFilterValues({
    primaryFilterKey: 'metrics',
    primaryFilterValues: primaryFilterSelections,
  });

  if (!metrics || !groupBy) {
    return null;
  }

  const filters = getFiltersFromSecondaryFilterSelections({
    secondaryFilterSelections,
  });

  return {
    customVizId: 'tableView',
    inputs: {
      extras: {
        groupBy,
        secondaryBreakdown,
        metrics,
        filters,
      },
      dropdowns: {},
    },
    observationLocationType: myObservation.type,
    organizationId,
  };
};

// eslint-disable-next-line complexity
export const getCustomVizFromFilters = (
  props: GetCustomVizFromFiltersProps,
): CustomVizRequest | null => {
  switch (props.feature) {
    case 'binScatterMultiSelect':
      return buildBinScatterMultiSelectViz(props);
    case 'employeeLifecycle':
      return buildPipelineAnalysisViz(props);
    case 'exploreHeatmap':
      return buildHeatmapViz(props);
    case 'dataIntelligenceDeepDive':
      return buildDeepDiveViz(props);
    case 'successFactors':
      return buildSuccessPhenotypeViz(props);
    case 'progressPage':
      return buildProgressViz(props);
    case 'summaryStats':
      return buildSummaryStatsViz(props);
    case 'richText':
      return buildRichTextViz(props);
    case 'tableView':
      return buildTableViewViz(props);
    case 'timeSeriesTrendsPrimary':
      return buildTimeSeriesTrendsViz(props);
    case 'geolocation':
      return buildGeolocationViz(props);
    case 'externalStreamlit':
      return buildExternalStreamlitViz(props);
    default:
      return null;
  }
};

const getProgressFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: ProgressRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  focusArea: FocusArea;
} => {
  const extras = customVizRequest.inputs.extras;
  const filters = extras.filters;
  const focusArea = extras.focusArea;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'progressPage',
      focusArea: extras.focusArea,
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections = getPrimaryFilterSelectionsFromRequest({
    request: customVizRequest,
  });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
    focusArea,
  };
};

const getPipelineAnalysisFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: PipelineAnalysisRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
} => {
  const primaryFilterSelections =
    getPipelineAnalysisPrimaryFilterSelectionsFromRequest({
      request: customVizRequest,
    });

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'employeeLifecycle',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: customVizRequest.inputs.extras.filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getHeatmapFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: HeatmapAnalysisRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
} => {
  const filters = customVizRequest.inputs.extras.filters;
  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'exploreHeatmap',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const getPrivilegedLevelForDimension = (columnHeaderValue: string) =>
    getPrivilegedLevelForDimensionFromObservation(
      myObservation,
      columnHeaderValue,
    );

  const getLevelByColumnValue = (
    columnHeaderValue: string,
    columnValue: string,
  ) =>
    getLevelByColumnValueFromObservation(
      myObservation,
      columnHeaderValue,
      columnValue,
    );

  const primaryFilterSelections = getExplorePrimaryFilterSelectionsFromRequest({
    request: customVizRequest,
    getPrivilegedLevelForDimension,
    getLevelByColumnValue,
  });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getDeepDiveFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: BiasAnalysisRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  focusArea: FocusArea;
} => {
  const extras = customVizRequest.inputs.extras;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'dataIntelligenceDeepDive',
      focusArea: extras.focusArea,
      customVizFilters: extras.filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const getPrivilegedLevelForDimension = (columnHeaderValue: string) =>
    getPrivilegedLevelForDimensionFromObservation(
      myObservation,
      columnHeaderValue,
    );

  const getLevelByColumnValue = (
    columnHeaderValue: string,
    columnValue: string,
  ) =>
    getLevelByColumnValueFromObservation(
      myObservation,
      columnHeaderValue,
      columnValue,
    );

  const primaryFilterSelections = getDeepDivePrimaryFilterSelectionsFromRequest(
    {
      request: customVizRequest,
      getPrivilegedLevelForDimension,
      getLevelByColumnValue,
    },
  );

  const primaryFilterSelectionsWithFocusArea: PrimaryFilterSelection[] = [
    {
      primaryFilterKey: 'focusArea',
      value: [extras.focusArea, extras.metric],
    },
    ...primaryFilterSelections,
  ];

  const focusArea = extras.focusArea;

  return {
    primaryFilterSelections: primaryFilterSelectionsWithFocusArea,
    secondaryFilterSelections,
    focusArea,
  };
};

export const getSuccessPhenotypeFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: SuccessPhenotypeV2Request;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
} => {
  const filters = customVizRequest.inputs.extras.filters;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'successFactors',
      focusArea: 'Performance',
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections = getSuccessFactorsPrimaryFilterSelections({
    request: customVizRequest,
  });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getBinScatterMultiSelectFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: BinScatterMultiSelectRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
} => {
  const extras = customVizRequest.inputs.extras;
  const filters = extras.filters;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'binScatterMultiSelect',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const { dimension, metric, focusArea } = extras;

  const primaryFilterSelections: PrimaryFilterSelection[] = [
    {
      primaryFilterKey: 'focusArea',
      value: [focusArea, metric],
    },
    {
      primaryFilterKey: 'dimension',
      value: dimension,
    },
  ];

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getTableViewFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: TableViewRequest;
  myObservation: MyObservation;
}) => {
  const filters = customVizRequest.inputs.extras.filters;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'tableView',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections = getTableViewPrimaryFilterSelections({
    request: customVizRequest,
  });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getRichTextFiltersFromCustomViz = () => {
  return {
    primaryFilterSelections: [],
    secondaryFilterSelections: [],
  };
};

const getExternalStreamlitFiltersFromCustomViz = () => {
  return {
    primaryFilterSelections: [],
    secondaryFilterSelections: [],
  };
};

const getSummaryStatsFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: SummaryStatsRequest;
  myObservation: MyObservation;
}) => {
  const filters = customVizRequest.inputs.extras.filters;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'summaryStats',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections = getSummaryStatsPrimaryFilterSelections({
    request: customVizRequest,
  });

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

const getTimeSeriesTrendsFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: TimeSeriesTrendsRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
} | null => {
  const filters = customVizRequest.inputs.extras.filters;

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'timeSeriesTrendsPrimary',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      customVizFilters: filters,
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections = getTimeSeriesTrendsPrimaryFilterSelections({
    customVizRequest,
    myObservation,
  });

  if (!primaryFilterSelections) {
    return null;
  }

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

export const getGeolocationFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: GeolocationRequest;
  myObservation: MyObservation;
}) => {
  const extras = customVizRequest.inputs.extras;
  const filters = extras.filters;
  const metrics = extras.metrics;

  const allMetrics = myObservation.focusAreas.flatMap((focusArea) =>
    focusArea.metrics.map((metric) => metric.columnHeaderValue),
  );

  const startDate = new Date(extras.startDate);
  const endDate = new Date(extras.endDate);
  const dateRangeType = extras.dateRangeType || 'all';

  const secondaryFilterSelections =
    getSecondaryFilterSelectionsFromCustomVizFormat({
      feature: 'geolocation',
      focusArea: resolveFocusAreaForCustomViz(customVizRequest),
      focusAreas: myObservation.focusAreas,
      filterSetDefinitionsWithValues:
        myObservation.filterSetDefinitionsWithValues,
      customVizFilters: filters,
      dimensionDefinitions: myObservation.dimensions,
      analysisName: resolveAnalysisNameForCustomViz(customVizRequest),
      pipelineAnalysisConfiguration:
        myObservation.pipelineAnalysisConfiguration,
    });

  const primaryFilterSelections: PrimaryFilterSelection[] = [
    {
      primaryFilterKey: 'metrics',
      value: metrics ? metrics : allMetrics,
    },
    {
      primaryFilterKey: 'dateRange',
      value: {
        startDate,
        endDate,
        dateRangeType,
      },
    },
  ];

  if (!primaryFilterSelections) {
    return null;
  }

  return {
    primaryFilterSelections,
    secondaryFilterSelections,
  };
};

// eslint-disable-next-line complexity
export const getFiltersFromCustomViz = ({
  customVizRequest,
  myObservation,
}: {
  customVizRequest: CustomVizRequest;
  myObservation: MyObservation;
}): {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  focusArea?: FocusArea;
} | null => {
  switch (customVizRequest.customVizId) {
    case 'progress':
      return getProgressFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'timeSeriesTrends':
      return getTimeSeriesTrendsFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'pipelineAnalysis':
      return getPipelineAnalysisFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });

    case 'heatmap':
      return getHeatmapFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });

    case 'biasAnalysis':
      return getDeepDiveFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });

    case 'successPhenotypeV2':
      return getSuccessPhenotypeFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });

    case 'binScatterMultiSelect':
      return getBinScatterMultiSelectFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'tableView':
      return getTableViewFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'richText':
      return getRichTextFiltersFromCustomViz();

    case 'summaryStats':
      return getSummaryStatsFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'geolocation':
      return getGeolocationFiltersFromCustomViz({
        customVizRequest,
        myObservation,
      });
    case 'externalStreamlit':
      return getExternalStreamlitFiltersFromCustomViz();
    default:
      return null;
  }
};
